/**
 * Client Portal Service
 * API service for client portal functionality
 */
import axios from 'axios';
import { goToLogin } from '../utils/navigation';
import { recordApiCall } from '../utils/portalDiagnostics';

const API_URL = '/api/client-portal';

// Create separate axios instance for client portal (different token storage)
const clientApi = axios.create({
  baseURL: API_URL
});

// Add token interceptor
clientApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
clientApi.interceptors.response.use(
  (response) => {
    try { recordApiCall(response.config?.method, response.config?.url, response.status); } catch (e) {}
    return response;
  },
  (error) => {
    try { recordApiCall(error.config?.method, error.config?.url, error.response?.status); } catch (e) {}
    if (error.response?.status === 401) {
      localStorage.removeItem('client_portal_token');
      localStorage.removeItem('client_portal_contact');
      // Soft-navigate, and never when the user is already ON a login route.
      // The code-entry screen makes its own pre-auth calls (the status banner
      // 401s by design); redirecting on those bounced the user off the screen
      // they had just reached, and window.location made it a hard document load
      // that host routing answered with a 404.
      goToLogin('/portal/login');
    }
    return Promise.reject(error);
  }
);

const clientPortalService = {
  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  async login(email, password) {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const {
      access_token, contact_id, contact_name, email: userEmail,
      is_first_login, profile_needs_update, intake_completed, pin_created, must_change_password
    } = response.data;

    // Only store credentials for direct login (no 2FA).
    // When requires_2fa is true, access_token is undefined - storing it
    // causes isAuthenticated() to briefly return true with a bad token,
    // triggering a 401 > logout > double-login cycle.
    if (access_token) {
      localStorage.setItem('client_portal_token', access_token);
      localStorage.setItem('client_portal_contact', JSON.stringify({
        contact_id,
        contact_name,
        email: userEmail,
        is_first_login,
        profile_needs_update,
        intake_completed,
        pin_created,
        must_change_password
      }));
    }

    return response.data;
  },

  // The identifier-first form authenticates via /api/auth/resolve-login, which
  // returns the same payload the legacy portal login does. PortalLogin owns the
  // code-entry step, so hand it the temp token rather than re-asking for the
  // password — re-asking issued a SECOND code and silently burned the first.
  stashPendingLogin(data) {
    if (!data) return;
    if (data.access_token) {
      localStorage.setItem('client_portal_token', data.access_token);
    }
    if (data.temp_token) {
      sessionStorage.setItem('portal_pending_token', data.temp_token);
    }
    if (data.contact_id) {
      sessionStorage.setItem('portal_pending_contact', JSON.stringify({
        contact_id: data.contact_id,
        email: data.email,
      }));
    }
  },

  async logout() {
    try {
      await clientApi.post('/auth/logout');
    } finally {
      localStorage.removeItem('client_portal_token');
      localStorage.removeItem('client_portal_contact');
    }
  },

  async changePassword(currentPassword, newPassword) {
    const response = await clientApi.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  isAuthenticated() {
    return !!localStorage.getItem('client_portal_token');
  },

  getClientInfo() {
    const info = localStorage.getItem('client_portal_contact');
    return info ? JSON.parse(info) : null;
  },

  needsProfileSetup() {
    const info = this.getClientInfo();
    return info?.is_first_login || info?.profile_needs_update || false;
  },

  clearProfileSetupFlag() {
    const info = this.getClientInfo();
    if (info) {
      info.is_first_login = false;
      info.profile_needs_update = false;
      localStorage.setItem('client_portal_contact', JSON.stringify(info));
    }
  },

  // ============================================================================
  // DASHBOARD & PROFILE
  // ============================================================================

  async getDashboard() {
    const response = await clientApi.get('/dashboard');
    return response.data;
  },

  async getProfile() {
    const response = await clientApi.get('/profile');
    return response.data;
  },

  async updateProfile(data) {
    const response = await clientApi.put('/profile', data);
    return response.data;
  },

  // ============================================================================
  // CASES
  // ============================================================================

  async getCases() {
    const response = await clientApi.get('/cases');
    return response.data;
  },

  async getCaseDetail(caseId) {
    const response = await clientApi.get(`/cases/${caseId}`);
    return response.data;
  },

  async getCaseDocuments(caseId) {
    const response = await clientApi.get(`/cases/${caseId}/documents`);
    return response.data;
  },

  // ============================================================================
  // DOCUMENTS
  // ============================================================================

  async uploadDocument(caseId, file, description = '', category = 'client_uploads') {
    const formData = new FormData();
    formData.append('file', file);

    const response = await clientApi.post(
      `/documents/upload?case_id=${caseId}&description=${encodeURIComponent(description)}&category=${category}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  async downloadDocument(documentId) {
    const response = await clientApi.get(`/documents/${documentId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // ============================================================================
  // MESSAGES
  // ============================================================================

  async getMessages(caseId = null, page = 1, pageSize = 20) {
    let url = `/messages?page=${page}&page_size=${pageSize}`;
    if (caseId) {
      url += `&case_id=${caseId}`;
    }
    const response = await clientApi.get(url);
    return response.data;
  },

  async sendMessage(caseId, content, subject = null, parentMessageId = null) {
    const response = await clientApi.post('/messages', {
      case_id: caseId,
      content,
      subject,
      parent_message_id: parentMessageId
    });
    return response.data;
  },

  async markMessageRead(messageId) {
    const response = await clientApi.post(`/messages/${messageId}/read`);
    return response.data;
  },

  async uploadMessageAttachment(messageId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await clientApi.post(`/messages/${messageId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async getMessageAttachments(messageId) {
    const response = await clientApi.get(`/messages/${messageId}/attachments`);
    return response.data;
  },

  async downloadMessageAttachment(attachmentId) {
    const response = await clientApi.get(`/messages/attachments/${attachmentId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // ============================================================================
  // ANNOUNCEMENTS
  // ============================================================================

  async getAnnouncements() {
    const response = await clientApi.get('/announcements');
    return response.data;
  },

  async dismissAnnouncement(announcementId) {
    const response = await clientApi.post(`/announcements/${announcementId}/dismiss`);
    return response.data;
  },

  // ============================================================================
  // INVOICES
  // ============================================================================

  async getInvoices(status = null, caseId = null) {
    let url = '/invoices';
    const params = [];
    if (status) params.push(`status=${status}`);
    if (caseId) params.push(`case_id=${caseId}`);
    if (params.length > 0) url += '?' + params.join('&');

    const response = await clientApi.get(url);
    return response.data;
  },

  async getInvoiceDetail(invoiceId) {
    const response = await clientApi.get(`/invoices/${invoiceId}`);
    return response.data;
  },

  async initiatePayment(invoiceId, amount = null, returnUrl = null) {
    const response = await clientApi.post(`/invoices/${invoiceId}/pay`, {
      invoice_id: invoiceId,
      amount,
      return_url: returnUrl
    });
    return response.data;
  },

  // ============================================================================
  // ============================================================================
  // EVIDENCE / CONSENT
  // ============================================================================

  async getEvidenceConsentRequests() {
    const response = await clientApi.get("/evidence/consent-requests");
    return response.data;
  },

  async getEvidenceConsentForm(consentId) {
    const response = await clientApi.get(`/evidence/consent-requests/${consentId}`);
    return response.data;
  },

  async signEvidenceConsent(consentId, signatureName) {
    const response = await clientApi.post(`/evidence/consent-requests/${consentId}/sign`, {
      signature_name: signatureName
    });
    return response.data;
  },

  async declineEvidenceConsent(consentId) {
    const response = await clientApi.post(`/evidence/consent-requests/${consentId}/decline`);
    return response.data;
  },

  async revokeEvidenceConsent(consentId) {
    const response = await clientApi.post(`/evidence/consent-requests/${consentId}/revoke`);
    return response.data;
  },

  async uploadEvidenceFile(consentId, file) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await clientApi.post(`/evidence/consent-requests/${consentId}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return response.data;
  },

  async getEvidenceExtractions() {
    const response = await clientApi.get("/evidence/extractions");
    return response.data;
  },

  async getEvidenceThreads(extractionId) {
    const response = await clientApi.get(`/evidence/extractions/${extractionId}/threads`);
    return response.data;
  },

  // ASTRAEA AI (Walled-off)
  // ============================================================================

  async chatWithAI(message) {
    const response = await clientApi.post(`/ai/chat?message=${encodeURIComponent(message)}`);
    return response.data;
  },

  async clearAIConversation() {
    const response = await clientApi.post('/ai/clear');
    return response.data;
  },

  async getAIContextPreview() {
    const response = await clientApi.get('/ai/context-preview');
    return response.data;
  },
  // ============================================================================
  // TWO-FACTOR AUTHENTICATION
  // ============================================================================



  async verify2FALogin(tempToken, code) {
    const response = await axios.post(`${API_URL}/auth/2fa/login-verify`, {
      temp_token: tempToken,
      code
    });
    const {
      access_token, contact_id, contact_name, email: userEmail,
      is_first_login, profile_needs_update, intake_completed, must_change_password
    } = response.data;

    localStorage.setItem('client_portal_token', access_token);
    localStorage.setItem('client_portal_contact', JSON.stringify({
      contact_id, contact_name, email: userEmail,
      is_first_login, profile_needs_update, intake_completed,
      pin_created: response.data.pin_created,
      must_change_password,
      staging_incomplete: response.data.staging_incomplete
    }));

    return response.data;
  },

  // ============================================================================
  // PIN MANAGEMENT
  // ============================================================================

  async setPin(pin) {
    const response = await clientApi.post('/set-pin', { pin });
    return response.data;
  },

  async changePin(currentPin, currentPassword, newPin) {
    const response = await clientApi.post('/change-pin', {
      current_pin: currentPin || null,
      current_password: currentPassword || null,
      new_pin: newPin
    });
    return response.data;
  },

  async getPinStatus() {
    const response = await clientApi.get('/pin-status');
    return response.data;
  },

  needsPinSetup() {
    const info = JSON.parse(localStorage.getItem('client_portal_contact') || '{}');
    return info.pin_created === false;
  },

  clearPinSetupFlag() {
    const info = JSON.parse(localStorage.getItem('client_portal_contact') || '{}');
    info.pin_created = true;
    localStorage.setItem('client_portal_contact', JSON.stringify(info));
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  async getNotifications(unreadOnly = false, limit = 50, offset = 0) {
    const response = await clientApi.get(`/notifications?unread_only=${unreadOnly}&limit=${limit}&offset=${offset}`);
    return response.data;
  },

  async markNotificationRead(id) {
    const response = await clientApi.post(`/notifications/${id}/read`);
    return response.data;
  },

  async markAllNotificationsRead() {
    const response = await clientApi.post('/notifications/read-all');
    return response.data;
  },

  async getNotificationPreferences() {
    const response = await clientApi.get('/notifications/preferences');
    return response.data;
  },

  async updateNotificationPreferences(prefs) {
    const response = await clientApi.put('/notifications/preferences', prefs);
    return response.data;
  },

  // ============================================================================
  // PAYMENT COMPLETION
  // ============================================================================

  async getPaymentComplete(transactionId, orderId, referenceId) {
    const params = new URLSearchParams();
    if (transactionId) params.append('transactionId', transactionId);
    if (orderId) params.append('orderId', orderId);
    if (referenceId) params.append('referenceId', referenceId);
    const response = await clientApi.get(`/payments/complete?${params.toString()}`);
    return response.data;
  },
  // ============================================================================
  // PAYMENTS — CARDS, PLANS, TRANSACTIONS
  // ============================================================================

  async getSquareConfig() {
    const response = await clientApi.get('/payments/square-config');
    return response.data;
  },

  async getSavedCards() {
    const response = await clientApi.get('/payments/cards');
    return response.data;
  },

  async addCard(cardNonce, cardholderName, autopayConsent = false) {
    const response = await clientApi.post('/payments/cards/add', {
      card_nonce: cardNonce,
      cardholder_name: cardholderName,
      autopay_consent: !!autopayConsent
    });
    return response.data;
  },

  async removeCard(cardId) {
    const response = await clientApi.delete(`/payments/cards/${cardId}`);
    return response.data;
  },

  async getPaymentPlans() {
    const response = await clientApi.get('/payments/plans');
    return response.data;
  },

  async createPaymentPlan(plan) {
    const response = await clientApi.post('/payments/plans', plan);
    return response.data;
  },

  async pausePlan(planId) {
    const response = await clientApi.put(`/payments/plans/${planId}/pause`);
    return response.data;
  },

  async resumePlan(planId) {
    const response = await clientApi.put(`/payments/plans/${planId}/resume`);
    return response.data;
  },

  async cancelPlan(planId) {
    const response = await clientApi.delete(`/payments/plans/${planId}`);
    return response.data;
  },

  async getTransactions(limit = 50, offset = 0) {
    const response = await clientApi.get(`/payments/transactions?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  async downloadReceipt(paymentId) {
    const response = await clientApi.get(`/payments/transactions/${paymentId}/receipt`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // ============================================================================
  // APPOINTMENTS
  // ============================================================================

  async getAvailableSlots(attorneyId, dateFrom, dateTo) {
    const params = new URLSearchParams();
    if (attorneyId) params.append('attorney_id', attorneyId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const response = await clientApi.get(`/appointments/available-slots?${params.toString()}`);
    return response.data;
  },

  async bookAppointment(data) {
    const response = await clientApi.post('/appointments/book', data);
    return response.data;
  },

  async getAppointments() {
    const response = await clientApi.get('/appointments');
    return response.data;
  },

  async rescheduleAppointment(id, startTime, endTime) {
    const response = await clientApi.put(`/appointments/${id}/reschedule`, {
      start_time: startTime,
      end_time: endTime
    });
    return response.data;
  },

  async cancelAppointment(id, reason) {
    const response = await clientApi.post(`/appointments/${id}/cancel`, { reason });
    return response.data;
  },

  // ============================================================================
  // ONBOARDING
  // ============================================================================

  async getOnboarding() {
    const response = await clientApi.get('/onboarding');
    return response.data;
  },

  async getOnboardingForCase(caseId) {
    const response = await clientApi.get(`/onboarding/${caseId}`);
    return response.data;
  },

  async completeOnboardingStep(caseId, stepId, data = null) {
    const response = await clientApi.post(`/onboarding/${caseId}/steps/${stepId}/complete`, { data });
    return response.data;
  },

  async skipOnboardingStep(caseId, stepId) {
    const response = await clientApi.post(`/onboarding/${caseId}/steps/${stepId}/skip`);
    return response.data;
  },

  // ============================================================================
  // QUESTIONNAIRES
  // ============================================================================

  async getQuestionnaires() {
    const response = await clientApi.get('/questionnaires');
    return response.data;
  },

  async getQuestionnaire(assignmentId) {
    const response = await clientApi.get(`/questionnaires/${assignmentId}`);
    return response.data;
  },

  async saveQuestionnaireResponses(assignmentId, responses) {
    const response = await clientApi.put(`/questionnaires/${assignmentId}/responses`, { responses });
    return response.data;
  },

  async submitQuestionnaire(assignmentId) {
    const response = await clientApi.post(`/questionnaires/${assignmentId}/submit`);
    return response.data;
  },

  async sendIntakeResumeLink(assignmentId) {
    const response = await clientApi.post(`/questionnaires/${assignmentId}/send-resume-link`);
    return response.data;
  },

  // ============================================================================
  // MANDATORY INTAKE
  // ============================================================================

  needsIntake() {
    const info = this.getClientInfo();
    return info && !info.intake_completed;
  },

  hasStagingIncomplete() {
    const info = this.getClientInfo();
    return info?.staging_incomplete === true;
  },

  clearStagingFlag() {
    const info = this.getClientInfo();
    if (info) {
      info.staging_incomplete = false;
      localStorage.setItem('client_portal_contact', JSON.stringify(info));
    }
  },

  async getIntakeAssignment() {
    const response = await clientApi.get('/intake/assignment');
    return response.data;
  },
  async getCaseTimeline(caseId) {
    // Whitelist-driven, firm-local case schedule (appointments + court dates +
    // litigation hearings only). Backend enforces per-contact isolation and the
    // client whitelist; internal events never reach the wire.
    const response = await clientApi.get(`/cases/${caseId}/timeline`);
    return response.data;
  },

  async getCalendarEvents(caseId, daysAhead = 90) {
    // clientApi baseURL is already /api/client-portal; the backend route is
    // /calendar/events. The old '/portal/calendar/events' doubled the prefix and
    // 404'd (silently swallowed by callers' .catch).
    const response = await clientApi.get('/calendar/events', {
      params: { case_id: caseId, days_ahead: daysAhead }
    });
    return response.data;
  },

  async getAvailabilitySummary() {
    const response = await clientApi.get('/appointments/availability-summary');
    return response.data;
  },

  // Support tickets — "Report a problem"
  async createTicket(body, diagnostics) {
    const response = await clientApi.post('/tickets', { body, diagnostics });
    return response.data;
  },
  async getMyTickets() {
    const response = await clientApi.get('/tickets');
    return response.data.tickets;
  },

  // Portal status banner (admin-set, shown portal-wide)
  async getBanner() {
    const response = await clientApi.get('/banner');
    return response.data;
  },

  // Court records auto-visible to this client (A4) — separate from shares
  async getCourtRecords() {
    const response = await clientApi.get('/court-records');
    return response.data.court_records;
  },
};
export default clientPortalService;